import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import Typography from "@mui/material/Typography";
import { Button, Skeleton, Stack } from "@mui/material";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import useApiHook from "../hooks/useApiHook";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import moment from "moment/moment";
import Transcription from "./Transcription";
import WebOnly from "./platforms/WebOnly";
import { useNotification } from "../store/notification-context";
import useAppView from "./platforms/useAppView";
import useDocumentVisibility from "../hooks/useDocumentVisibility";

moment.locale('it');

const FileDetails = () => {
    const { response, makeRequest, error } = useApiHook();
    const { fileId } = useParams();
    const navigate = useNavigate();
    const { notifyTranscriptionCompleted } = useNotification();
    const isDocumentVisible = useDocumentVisibility();

    const [fileDetails, setFileDetails] = useState(null);
    const [transcriptions, setTranscriptions] = useState([]);

    const pollingIntervalRef = useRef(null);
    const previousStatusRef = useRef(null);
    const backgroundedDuringPollingRef = useRef(false);
    const { isAppView } = useAppView();

    const deleteBtnClickHandler = () => {
        makeRequest('/file/delete', 'post', { fileId });
        navigate('/');
    }

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollingIntervalRef.current = setInterval(() => {
            makeRequest(`/file/${fileId}/details`);
        }, 3000);
    }, [fileId, makeRequest, stopPolling]);

    useEffect(() => {
        makeRequest(`/file/${fileId}/details`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isDocumentVisible && previousStatusRef.current === 'waiting') {
            backgroundedDuringPollingRef.current = true;
        }
    }, [isDocumentVisible]);

    useEffect(() => {
        console.log('FileDetails - response:', response);
        console.log('FileDetails - response type:', typeof response);
        console.log('FileDetails - error:', error);

        if (!response || typeof response === 'string') {
            console.log('Invalid response, skipping');
            return;
        }

        if (response && response.state === 'success') {
            response.data.formattedDate = moment(response.data.createdAt).format('D MMMM YYYY, HH:mm:ss');

            const currentStatus = response.data.status;

            if (previousStatusRef.current === 'waiting' && currentStatus === 'done') {
                if (!isDocumentVisible || !backgroundedDuringPollingRef.current) {
                    notifyTranscriptionCompleted({
                        fileId: response.data.id,
                        fileName: response.data.name
                    });
                }
            }

            previousStatusRef.current = currentStatus;
            if (currentStatus !== 'waiting') {
                backgroundedDuringPollingRef.current = false;
            }

            if (currentStatus === 'waiting') {
                startPolling();
            } else if (currentStatus === 'done') {
                stopPolling();
            }

            if (response.data.transcriptions && response.data.transcriptions.length > 0) {
                setTranscriptions([]);
                response.data.transcriptions.forEach((tr) => {
                    setTranscriptions(prevState => [...prevState, tr]);
                })

            }
            setFileDetails(response.data);
        }
    }, [isDocumentVisible, notifyTranscriptionCompleted, response, error, startPolling, stopPolling])

    useEffect(() => {
        if (error) {
            console.error('Error loading file details:', error);
            navigate('/');
        }
    }, [error, navigate])

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    const transcriptionsList = transcriptions.map(tr => {
        return <Transcription key={tr.id} fileName={fileDetails.name} transcription={tr} />;
    });

    return <>
        {!fileDetails && <Skeleton variant='rectangular' height={42} ></Skeleton>}
        {fileDetails && <>
            <Stack spacing={2} direction={{ xs: 'column', sm: "row" }} alignItems="center" >
                <WebOnly><LibraryBooksIcon fontSize="large" /></WebOnly>
                <Typography variant={isAppView ? 'h7' : 'h4'} fontWeight='bold' flexGrow={1}>{fileDetails.name}</Typography>
                <Button
                    size="large"
                    variant="contained"
                    color={"error"}
                    onClick={deleteBtnClickHandler}
                    startIcon={<DeleteForeverIcon />}>
                    Elimina File
                </Button>
            </Stack>
        </>}
        <Box my={1}>
            {!fileDetails && <Skeleton variant='rectangular' height={24} ></Skeleton>}
            {fileDetails && <Typography variant='body1'>
                {fileDetails.formattedDate}
            </Typography>}
        </Box>
        <Box my={3}>
            {(!transcriptions.length && !fileDetails) && <Skeleton variant='rectangular' height={150} ></Skeleton>}
            {(!transcriptions.length && fileDetails) && <Typography variant='h6'>Generazione testo in corso...</Typography>}
            {(transcriptions.length > 0 && fileDetails) && <Stack direction={{ xs: 'column', sm: "row" }} spacing={3}>
                {transcriptionsList}
            </Stack>}
        </Box>
    </>;
}

export default FileDetails;
